// import { useGoals } from '../hooks/useGoals';
import React, { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { DashboardStats, Interacao } from '../types/api';
import { useBotStatus } from '../hooks/useBotStatus';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UsersRound,
  ShoppingBag,
  CircleDollarSign,
  LineChart,
  UserX,
  Bot,
  UserRound,
  Code,
  Receipt,
  Calendar,
  ChevronDown
} from 'lucide-react';
import styled from 'styled-components';
import { useTheme } from 'styled-components';
import { useDashboardStats, useInteracoesRecentes, useVendas, useLeadsNovosHoje, useVendasHoje, useInteracoesBotUltimas24h, queryKeys, useLeads, useScripts } from '../hooks/useApi';
import { useCurrentUserId } from '../hooks/useCurrentUserId';

import StatsCard from '../components/Dashboard/StatsCard';
import ActivityTimeline from '../components/Dashboard/ActivityTimeline';
import ClassificationChart from '../components/Dashboard/ClassificationChart';
import DashboardGear from '../components/Dashboard/DashboardGear';
import SkeletonLoader from '../components/SkeletonLoader';
import FadeInContent from '../components/FadeInContent';
import { useSkeletonDelay } from '../hooks/useSkeletonDelay';
// import Dashboard3DCube from '../components/Dashboard/Dashboard3DCube';
// Utilitário para formatar moeda BRL igual Vendas, sem centavos se for zero
const formatCurrency = (value: number) => {
  const formatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  // Remove ,00 se não houver centavos
  return formatted.replace(',00', '');
};


// Todos os styled-components agora usam props.theme
const DashboardContainer = styled.div`
  padding: 0;
  max-width: 100%;
  margin: 0;
  background: ${({ theme }) => theme.colors.background};
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
`;





interface ToolbarProps {
  $active?: boolean;
}
const SelectorContainer = styled.div`
  position: relative;
  display: inline-block;
  z-index: 100;
`;

const SelectorButton = styled(motion.button) <{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: 8px 16px;
  background: ${({ theme, $active }) =>
    theme.isLight
      ? ($active ? 'rgba(0, 0, 0, 0.05)' : '#ffffff')
      : ($active ? 'rgba(255, 255, 255, 0.05)' : 'rgba(21, 21, 21, 0.4)')};
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.colors.error + '44' : (theme.isLight ? theme.colors.border : 'rgba(255, 255, 255, 0.08)')};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  &:hover {
    border-color: ${({ theme }) => theme.colors.error}66;
    background: ${({ theme }) => theme.isLight ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.03)'};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  svg:last-child {
    transition: transform 0.3s ease;
    ${({ $active }) => $active && 'transform: rotate(180deg);'}
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 180px;
  background: ${({ theme }) =>
    theme.isLight
      ? '#ffffff'
      : 'rgba(15, 15, 15, 0.95)'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid ${({ theme }) => theme.isLight ? theme.colors.border : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  padding: 6px;
  z-index: 101;
`;

const DropdownOption = styled.button<{ $isActive: boolean }>`
  width: 100%;
  padding: 10px 14px;
  background: ${({ $isActive, theme }) =>
    $isActive ? (theme.isLight ? 'rgba(255, 59, 48, 0.08)' : 'rgba(255, 59, 48, 0.12)') : 'transparent'};
  border: none;
  border-radius: 10px;
  color: ${({ $isActive, theme }) => $isActive ? theme.colors.error : theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ $isActive }) => $isActive ? '700' : '500'};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${({ $isActive, theme }) =>
    $isActive ? (theme.isLight ? 'rgba(255, 59, 48, 0.08)' : 'rgba(255, 59, 48, 0.12)') : (theme.isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.03)')};
    color: ${({ $isActive, theme }) => $isActive ? theme.colors.error : theme.colors.textPrimary};
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const DashboardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => `${theme.spacing[6]} ${theme.spacing[6]}`};
  background: ${({ theme }) =>
    theme.isLight
      ? '#ffffff'
      : 'rgba(21, 21, 21, 0.4)'};
  backdrop-filter: ${({ theme }) => theme.isLight ? 'none' : 'blur(20px)'};
  -webkit-backdrop-filter: ${({ theme }) => theme.isLight ? 'none' : 'blur(20px)'};
  border: 1px solid ${({ theme }) => theme.isLight ? theme.colors.border : 'rgba(255, 255, 255, 0.03)'};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  box-shadow: ${({ theme }) => theme.isLight ? '0 2px 12px rgba(0, 0, 0, 0.08)' : '0 4px 24px -1px rgba(0, 0, 0, 0.2)'};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  width: 100%;
  position: relative;
  z-index: 10;
  overflow: visible;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${({ theme }) => `linear-gradient(90deg, transparent, ${theme.colors.error}44, transparent)`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[5]}`};
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
`;

const HeaderTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};

  h1 {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;



const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: 700;
  margin: 0 0 ${({ theme }) => theme.spacing[5]} 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.01em;
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};

  &::before {
    content: '';
    width: 4px;
    height: 18px;
    background: ${({ theme }) => theme.colors.error};
    border-radius: ${({ theme }) => theme.borders.radius.full};
  }

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => `linear-gradient(90deg, ${theme.colors.border} 0%, transparent 100%)`};
    opacity: 0.3;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing[4]} 0;
  opacity: 0.6;
`;

const StatsGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  grid-template-columns: 1fr;
  width: 100%;
  max-width: 100%;
  overflow: visible;
  padding: ${({ theme }) => theme.spacing[1]};

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${({ theme }) => theme.spacing[4]};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  width: 100%;
  max-width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
    gap: ${({ theme }) => theme.spacing[6]};
  }
`;

const RealTimeSection = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
  grid-template-columns: 1fr;
  width: 100%;
  max-width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1.5fr 1fr;
    gap: ${({ theme }) => theme.spacing[6]};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    grid-template-columns: 2fr 1fr;
  }
`;

const RealTimeStats = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: 1fr;
  width: 100%;
  max-width: 100%;
  overflow: visible;
  padding: ${({ theme }) => theme.spacing[1]};

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const RealTimeCard = styled(motion.div)`
  background: ${({ theme }) =>
    theme.isLight
      ? '#ffffff'
      : 'rgba(21, 21, 21, 0.7)'};
  backdrop-filter: ${({ theme }) => theme.isLight ? 'none' : 'blur(12px)'};
  -webkit-backdrop-filter: ${({ theme }) => theme.isLight ? 'none' : 'blur(12px)'};
  border: 1px solid ${({ theme }) => theme.isLight ? theme.colors.border : 'rgba(255, 255, 255, 0.05)'};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  padding: ${({ theme }) => theme.spacing[5]};
  text-align: center;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.isLight ? '0 4px 12px rgba(0, 0, 0, 0.05)' : '0 8px 32px 0 rgba(0, 0, 0, 0.3)'};
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${({ theme }) =>
    theme.isLight
      ? theme.shadows.xl
      : `0 24px 48px -12px rgba(0, 0, 0, 0.6), 0 0 20px ${theme.colors.error}10`};
    border-color: ${({ theme }) => theme.colors.error}88;

    &::before {
      width: 100%;
      height: 4px;
      background: ${({ theme }) => theme.colors.error};
      box-shadow: 0 0 20px ${({ theme }) => theme.colors.error}66;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 30px;
    height: 3px;
    background: ${({ theme }) => theme.colors.error};
    opacity: 0.8;
    transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    border-radius: 0 0 ${({ theme }) => theme.borders.radius.sm} 0;
  }
`;

const RealTimeValue = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  margin-top: ${({ theme }) => theme.spacing[2]};
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  z-index: 2;
  letter-spacing: -0.01em;
  line-height: 1;

  ${RealTimeCard}:hover & {
    transform: scale(1.1) translateY(-2px);
    color: ${({ theme }) => theme.colors.error};
    text-shadow: ${({ theme }) => theme.isLight ? 'none' : `0 0 25px ${theme.colors.error}44`};
  }
`;

const RealTimeLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  ${RealTimeCard}:hover & {
    color: ${({ theme }) => theme.colors.textPrimary};
    letter-spacing: 0.12em;
  }
`;




interface DashboardProps {
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ themeMode, onToggleTheme }) => {
  const theme = useTheme();
  const { userId } = useCurrentUserId();


  // TODOS os hooks devem vir ANTES de qualquer return!
  React.useEffect(() => {
    // Aplica as cores do tema no body
    if (themeMode === 'light') {
      document.body.style.backgroundColor = '#fff';
      document.body.style.color = '#232526';
    } else {
      document.body.style.backgroundColor = theme.colors.background;
      document.body.style.color = theme.colors.textPrimary;
    }
  }, [themeMode, theme.colors.background, theme.colors.textPrimary]);

  const queryClient = useQueryClient();
  const handleRealtimeEvent = React.useCallback((event: { type: string; payload: unknown }) => {
    const t = event.type;
    if (t && (t === 'dashboard_update' || t === 'lead_update' || t === 'lead_created' || t === 'lead_deleted')) {
      // Invalida e força refetch das queries relacionadas
      const keys = [
        queryKeys.dashboardStats,
        queryKeys.interacoes,
        queryKeys.vendas,
        queryKeys.vendasTotal,
        queryKeys.leadsNovosHoje,
        queryKeys.vendasHoje,
        queryKeys.interacoesBotUltimas24h,
        queryKeys.leads,
      ];
      for (const key of keys) {
        queryClient.invalidateQueries({ queryKey: key });
        queryClient.refetchQueries({ queryKey: key, type: 'active' });
      }
    }
  }, [queryClient]);

  const { isLoading: botLoading, isActive: botActive, error: botError } = useBotStatus();
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: interacoes, isLoading: interacoesLoading } = useInteracoesRecentes();
  const { data: vendasData, isLoading: vendasLoading } = useVendas();
  const { data: leadsAllData, isLoading: leadsAllLoading } = useLeads();
  const { data: leadsHojeData, isLoading: leadsHojeLoading } = useLeadsNovosHoje();
  const { data: vendasHojeData, isLoading: vendasHojeLoading } = useVendasHoje();
  const { data: bot24hData, isLoading: bot24hLoading } = useInteracoesBotUltimas24h();
  const { data: scriptsData, isLoading: scriptsLoading } = useScripts(userId || undefined);

  // Hook para adicionar delay mínimo no skeleton (suavização)
  const showSkeleton = useSkeletonDelay(botLoading || !botActive || !!botError, 1500);
  const [period, setPeriod] = React.useState<'today' | '7d' | '30d' | '90d'>('today');
  const [periodDropdownOpen, setPeriodDropdownOpen] = React.useState(false);

  const getPeriodLabel = (p: string) => {
    switch (p) {
      case 'today': return 'Hoje';
      case '7d': return 'Últimos 7 dias';
      case '30d': return 'Últimos 30 dias';
      case '90d': return 'Últimos 90 dias';
      default: return p;
    }
  };

  const periodStart = useMemo(() => {
    const now = new Date();
    const d = new Date(now);
    switch (period) {
      case 'today':
        d.setHours(0, 0, 0, 0);
        return d;
      case '7d':
        d.setDate(d.getDate() - 7);
        return d;
      case '30d':
        d.setDate(d.getDate() - 30);
        return d;
      case '90d':
        d.setDate(d.getDate() - 90);
        return d;
      default:
        return d;
    }
  }, [period]);

  const filteredLeads = useMemo(() => {
    const all = leadsAllData?.data?.leads || [];
    return all.filter(l => {
      const t = new Date(l.dataCriacao).getTime();
      return t >= periodStart.getTime();
    });
  }, [leadsAllData, periodStart]);

  const filteredVendasPagas = useMemo(() => {
    const all = vendasData?.data?.vendas || [];
    return all.filter(v => {
      const t = new Date(v.dataVenda).getTime();
      return v.status === 'pago' && t >= periodStart.getTime();
    });
  }, [vendasData, periodStart]);

  const kpi = useMemo(() => {
    // KPIs derivados do período
    const totalLeads = filteredLeads.length;
    const leadsNovo = filteredLeads.filter(l => l.status === 'novo').length;
    const leadsQualificado = filteredLeads.filter(l => l.status === 'qualificado').length;
    const leadsProspect = filteredLeads.filter(l => l.status === 'prospect').length;
    const leadsConvertido = filteredLeads.filter(l => l.status === 'convertido').length;
    const leadsPerdido = filteredLeads.filter(l => l.status === 'perdido').length;
    const vendasPagas = filteredVendasPagas.length;
    const valorTotalVendas = filteredVendasPagas.reduce((sum, v) => sum + (parseFloat(v.valor.replace('R$', '').replace(',', '.')) || 0), 0);
    const taxaConversao = totalLeads > 0 ? Math.round((leadsConvertido / totalLeads) * 100) : 0;
    return { totalLeads, leadsNovo, leadsQualificado, leadsProspect, leadsConvertido, leadsPerdido, vendasPagas, valorTotalVendas, taxaConversao };
  }, [filteredLeads, filteredVendasPagas]);

  const funnelSteps = useMemo(() => {
    const data: DashboardStats | undefined = stats?.data;
    // Se dados por período disponíveis, usa-os; senão fallback para stats
    const usePeriod = !leadsAllLoading && !vendasLoading;
    type PeriodValues = {
      leadsNovo: number;
      leadsQualificado: number;
      leadsProspect: number;
      leadsConvertido: number;
      leadsPerdido: number;
    };
    const values: PeriodValues = usePeriod
      ? {
        leadsNovo: kpi.leadsNovo,
        leadsQualificado: kpi.leadsQualificado,
        leadsProspect: kpi.leadsProspect,
        leadsConvertido: kpi.leadsConvertido,
        leadsPerdido: kpi.leadsPerdido,
      }
      : {
        leadsNovo: data?.leadsNovo || 0,
        leadsQualificado: data?.leadsQualificado || 0,
        leadsProspect: data?.leadsProspect || 0,
        leadsConvertido: data?.leadsConvertido || 0,
        leadsPerdido: data?.leadsPerdido || 0,
      };
    return [
      { id: 'novo', title: 'Novo', description: 'Leads recém-capturados', value: values.leadsNovo || 0, icon: UsersRound as unknown as React.ElementType, level: 1 },
      { id: 'qualificado', title: 'Qualificado', description: 'Leads qualificados', value: values.leadsQualificado || 0, icon: UserX as unknown as React.ElementType, level: 2 },
      { id: 'prospect', title: 'Prospect', description: 'Leads em prospecção', value: values.leadsProspect || 0, icon: UsersRound as unknown as React.ElementType, level: 3 },
      { id: 'convertido', title: 'Convertido', description: 'Leads convertidos em clientes', value: values.leadsConvertido || 0, icon: ShoppingBag as unknown as React.ElementType, level: 4 },
      { id: 'perdido', title: 'Perdido', description: 'Leads perdidos', value: values.leadsPerdido || 0, icon: UserRound as unknown as React.ElementType, level: 5 },
    ];
  }, [stats, kpi, leadsAllLoading, vendasLoading]);

  // Agora o backend já retorna stacks agrupados!
  const activities = useMemo(() => {
    if (!interacoes?.data?.interacoes) return [];
    return interacoes.data.interacoes.map((i: Interacao) => ({
      id: String(i.id),
      type: i.origem === 'BOT' ? 'bot' : 'message' as 'bot' | 'message',
      title: i.lead || 'Interação',
      description: i.mensagem,
      timestamp: i.data
    }));
  }, [interacoes]);


  // Ticket médio
  const ticketMedio = useMemo(() => {
    if (!leadsAllLoading && !vendasLoading) {
      const totalValor = kpi.valorTotalVendas;
      const vendasPagasCount = filteredVendasPagas.length;
      return vendasPagasCount > 0 ? totalValor / vendasPagasCount : 0;
    }
    const totalValorFallback = typeof stats?.data?.valorTotalVendas === 'number' ? stats.data.valorTotalVendas : 0;
    const vendasPagasCountFallback = vendasData?.data?.vendas?.filter(v => v.status === 'pago').length || 0;
    return vendasPagasCountFallback > 0 ? totalValorFallback / vendasPagasCountFallback : 0;
  }, [kpi, filteredVendasPagas, stats, leadsAllLoading, vendasLoading, vendasData]);

  // Total de scripts ativos
  const totalScriptsAtivos = useMemo(() => {
    if (!scriptsData?.data) return 0;

    const conteudoCount = Object.keys(scriptsData.data.conteudo || {}).length;
    const mentalidadesCount = Object.keys(scriptsData.data.mentalidades || {}).length;
    const fatosProdutoCount = scriptsData.data.fatos_produto && scriptsData.data.fatos_produto.trim() ? 1 : 0;

    return conteudoCount + mentalidadesCount + fatosProdutoCount;
  }, [scriptsData]);




  // Fallback: refetch periódico se a aba estiver visível (cobre casos onde o WS não emite evento)
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        queryClient.refetchQueries({ queryKey: queryKeys.leads, type: 'active' });
        queryClient.refetchQueries({ queryKey: queryKeys.dashboardStats, type: 'active' });
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [queryClient]);

  // Returns de loading/erro SEMPRE depois dos hooks com delay suave
  if (showSkeleton) {
    return <SkeletonLoader variant="dashboard" />;
  }

  return (
    <FadeInContent>
      <DashboardContainer>
        <DashboardHeader>
          <HeaderTop>
            <HeaderTitleBlock>
              <h1>Painel de Controle</h1>
              <p>Visão geral do desempenho e atividades do bot</p>
            </HeaderTitleBlock>
            <HeaderActions>
              <DashboardGear themeMode={themeMode} onToggleTheme={onToggleTheme} />
            </HeaderActions>
          </HeaderTop>

          {/* Seletor de período profissional */}
          <Toolbar>
            <SelectorContainer>
              <SelectorButton
                onClick={() => setPeriodDropdownOpen(!periodDropdownOpen)}
                $active={periodDropdownOpen}
                whileTap={{ scale: 0.98 }}
              >
                <Calendar size={16} color={theme.colors.error} />
                <span>{getPeriodLabel(period)}</span>
                <ChevronDown size={14} style={{ opacity: 0.5 }} />
              </SelectorButton>

              <AnimatePresence>
                {periodDropdownOpen && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setPeriodDropdownOpen(false)}
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 99
                      }}
                    />
                    <DropdownMenu
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    >
                      {[
                        { id: 'today', label: 'Hoje' },
                        { id: '7d', label: 'Últimos 7 dias' },
                        { id: '30d', label: 'Últimos 30 dias' },
                        { id: '90d', label: 'Últimos 90 dias' }
                      ].map((opt) => (
                        <DropdownOption
                          key={opt.id}
                          $isActive={period === opt.id}
                          onClick={() => {
                            setPeriod(opt.id as any);
                            setPeriodDropdownOpen(false);
                          }}
                        >
                          {opt.label}
                          {period === opt.id && (
                            <div style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: theme.colors.error,
                              boxShadow: `0 0 8px ${theme.colors.error}88`
                            }} />
                          )}
                        </DropdownOption>
                      ))}
                    </DropdownMenu>
                  </>
                )}
              </AnimatePresence>
            </SelectorContainer>
          </Toolbar>
        </DashboardHeader>

        <SectionTitle>Visão Geral de Performance</SectionTitle>
        <StatsGrid>
          <StatsCard
            title="Leads Capturados"
            value={(!leadsAllLoading ? kpi.totalLeads : (stats?.data?.totalLeads || 0)).toLocaleString?.() || String(!leadsAllLoading ? kpi.totalLeads : (stats?.data?.totalLeads || 0))}
            icon={UsersRound}
            iconColor={theme.colors.info}
            description="Total de prospects identificados"
            loading={statsLoading}
            $delay={0}
          />
          <StatsCard
            title="Leads Perdidos"
            value={(!leadsAllLoading ? kpi.leadsPerdido : (stats?.data?.leadsPerdido || 0)).toLocaleString?.() || String(!leadsAllLoading ? kpi.leadsPerdido : (stats?.data?.leadsPerdido || 0))}
            icon={UserX}
            iconColor={theme.colors.error}
            description="Oportunidades não convertidas"
            loading={statsLoading}
            $delay={0.1}
          />
          <StatsCard
            title="Faturamento Gerado"
            value={!leadsAllLoading && !vendasLoading ? formatCurrency(kpi.valorTotalVendas) : (stats && stats.data && typeof stats.data.valorTotalVendas === 'number'
              ? formatCurrency(stats.data.valorTotalVendas)
              : 'R$ 0,00')}
            icon={CircleDollarSign}
            iconColor={theme.colors.error}
            description="Faturamento total do período"
            loading={statsLoading}
            $delay={0.2}
          />
          <StatsCard
            title="Taxa de Sucesso"
            value={!leadsAllLoading ? `${kpi.taxaConversao}%` : (stats && stats.data && stats.data.taxaConversao ? `${stats.data.taxaConversao}%` : '0%')}
            icon={LineChart}
            iconColor={theme.colors.warning}
            description="Performance do funil de vendas"
            loading={statsLoading}
            $delay={0.3}
          />
          <StatsCard
            title="Pipeline Ativo"
            value={stats?.data?.leadsAtivos?.toLocaleString?.() || (stats?.data?.leadsAtivos ?? 0)}
            icon={UserX}
            iconColor={theme.colors.success}
            description="Oportunidades em progresso"
            loading={statsLoading}
            $delay={0.4}
          />
          <StatsCard
            title="Valor Médio"
            value={formatCurrency(ticketMedio)}
            icon={Receipt}
            iconColor={theme.colors.info}
            description="Ticket médio por conversão"
            loading={statsLoading || vendasLoading}
            $delay={0.5}
          />
        </StatsGrid>

        <Divider />

        <SectionTitle>Funil de Classificação</SectionTitle>
        <div>
          <ClassificationChart
            steps={funnelSteps}
            loading={statsLoading}
          />
        </div>

        <Divider />
        <SectionTitle>Atividade em Tempo Real</SectionTitle>
        <RealTimeSection>
          <ActivityTimeline
            activities={activities.slice(0, 6)}
            loading={interacoesLoading}
          />
          <div>
            <RealTimeStats>
              <RealTimeCard
                initial={{ opacity: 0, y: 40, scale: 0.92, rotateX: 10 }}
                animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 0.1,
                  ease: [0.34, 1.56, 0.64, 1]
                }}
                whileTap={{ scale: 0.97 }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Bot size={32} color={theme.colors.info} style={{ transition: 'all 0.3s' }} />
                  <RealTimeValue>
                    {(bot24hLoading || interacoesLoading)
                      ? '...'
                      : (
                        typeof bot24hData?.data?.count === 'number'
                          ? bot24hData.data.count
                          : (
                            (interacoes && interacoes.data && Array.isArray(interacoes.data.interacoes))
                              ? interacoes.data.interacoes.filter((i: import('../types/api').Interacao) => {
                                const t = new Date(i.data).getTime();
                                return i.origem === 'bot' && (Date.now() - t <= 24 * 60 * 60 * 1000);
                              }).length
                              : 0
                          )
                      )}
                  </RealTimeValue>
                  <RealTimeLabel>Conversas IA (24h)</RealTimeLabel>
                </div>
              </RealTimeCard>

              <RealTimeCard
                initial={{ opacity: 0, y: 40, scale: 0.92, rotateX: 10 }}
                animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 0.2,
                  ease: [0.34, 1.56, 0.64, 1]
                }}
                whileTap={{ scale: 0.97 }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Code size={32} color={theme.colors.error} style={{ transition: 'all 0.3s' }} />
                  <RealTimeValue>
                    {scriptsLoading ? '...' : totalScriptsAtivos}
                  </RealTimeValue>
                  <RealTimeLabel>Scripts Ativos</RealTimeLabel>
                </div>
              </RealTimeCard>

              <RealTimeCard
                initial={{ opacity: 0, y: 40, scale: 0.92, rotateX: 10 }}
                animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 0.3,
                  ease: [0.34, 1.56, 0.64, 1]
                }}
                whileTap={{ scale: 0.97 }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <UserRound size={32} color={theme.colors.success} style={{ transition: 'all 0.3s' }} />
                  <RealTimeValue>
                    {(leadsHojeLoading || statsLoading) ? '...' : (typeof leadsHojeData?.data?.count === 'number' ? leadsHojeData.data.count : (typeof stats?.data?.leadsAtivos === 'number' ? stats.data.leadsAtivos : 0))}
                  </RealTimeValue>
                  <RealTimeLabel>Novos Prospects</RealTimeLabel>
                </div>
              </RealTimeCard>

              <RealTimeCard
                initial={{ opacity: 0, y: 40, scale: 0.92, rotateX: 10 }}
                animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 0.4,
                  ease: [0.34, 1.56, 0.64, 1]
                }}
                whileTap={{ scale: 0.97 }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ShoppingBag size={32} color={theme.colors.warning} style={{ transition: 'all 0.3s' }} />
                  <RealTimeValue>
                    {(vendasHojeLoading || vendasLoading) ? '...' : (typeof vendasHojeData?.data?.count === 'number' ? vendasHojeData.data.count : 0)}
                  </RealTimeValue>
                  <RealTimeLabel>Conversões Hoje</RealTimeLabel>
                </div>
              </RealTimeCard>

            </RealTimeStats>
          </div>
        </RealTimeSection>

      </DashboardContainer>
    </FadeInContent>
  );
};

export default Dashboard;
